/* eslint-disable no-console */
import {
  RequestDetails,
  PropertyLoanDetails,
  KeyLoanDetails,
  OtherLoanDetails,
  BorrowerExtends,
  CMHCLowRatioDataLoanDetailsExtends
} from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/types';
import { formatAddress } from 'Feature/V2/HbtPortfolioManagement/utils/FormatAddress';
import formatAmountWithDollar from 'Feature/V2/HbtPortfolioManagement/utils/FormatAmountWithDollar';
import { formatRatio } from 'Feature/V2/HbtPortfolioManagement/utils/FormatRatio';
import i18n from 'i18next';
import { MasterPoolLoanDetails } from '../types';
import { InsuredApprovedLenderName } from '../../HbtMasterPool/types';

interface HbtMaterPoolIndividualLoanDetailsMapperType {
  borrowerLoanDetails: BorrowerExtends[];
  lenderDetails: RequestDetails;
  propertyDetails: PropertyLoanDetails;
  keyLoanDetails: KeyLoanDetails;
  cmhcLowRatioDetails?: CMHCLowRatioDataLoanDetailsExtends;
  otherLoanData?: OtherLoanDetails;
}

export const HbtMaterPoolIndividualLoanDetailsMapper = (
  data: MasterPoolLoanDetails
): HbtMaterPoolIndividualLoanDetailsMapperType => {
  const borrowerLoanDetails: BorrowerExtends[] = data.borrowers.map((b) => {
    const name = b?.firstName
      ? `${b?.lastName ?? ''}, ${b?.firstName ?? ''}`
      : `${b?.lastName ?? ''}`;
    return {
      name,
      dateOfBirth: b?.dateOfBirth || '',
      address: formatAddress(b.address),
      lenderCreditScore:
        b.typeId === 1
          ? data.borrowerLenderCreditScore || ''
          : data.coBorrowerLenderCreditScore || '',
      creditScore: '',
      CMHCCreditScore: b.typeId === 1 ? (b.creditScore ?? '') : (b.creditScore ?? ''),
      totalIncome:
        b.typeId === 1
          ? formatAmountWithDollar(data.borrowerTotalIncome.toString() || '', i18n.language)
          : formatAmountWithDollar(data.coBorrowerTotalIncome.toString() || '', i18n.language)
    };
  });
  const lenderDetails: RequestDetails = {
    portfolioId: data.portfolioID?.toString() ?? '',
    insuredApprovedLenderName: {
      englishName: data.insuredLenderCode,
      frenchName: data.insuredLenderCode
    } as InsuredApprovedLenderName,
    underwritingLenderName: {
      englishName: data.underwritingLenderName.englishName,
      frenchName: data.underwritingLenderName.frenchName
    } as InsuredApprovedLenderName
  };

  const propertyDetails: PropertyLoanDetails = {
    address: formatAddress(data.propertyAddress),
    locationType: data.propertyUrbanRuralID || '',
    occupancyType: data.propertyOccupancyTypeCode ?? '',
    tenureType: data.propertyTenureTypeCode || '',
    noOfUnits: data.propertyTotalUnitCount.toString() || '',
    dwellingType: data.propertyDwellingTypeCode || '',
    legalDescription: data.propertyLocationLegalDescriptionEnglish
  };

  const currentLTVRatio = (data.lenderLoanAmount / data.propertyMarketValueAmount) * 100;
  const keyLoanDetails: KeyLoanDetails = {
    originalLendingValue: formatAmountWithDollar(
      data.propertyMarketValueAmount?.toString() || '',
      i18n.language
    ),
    originalLoanAmount: formatAmountWithDollar(
      data.originalLoanPrincipalAmount?.toString() || '',
      i18n.language
    ),
    originalLTVRatio: formatRatio(data.loanToValueRatioPercent, i18n.language),
    currentLoanAmount: formatAmountWithDollar(
      data.lenderLoanAmount?.toString() || '',
      i18n.language
    ),
    currentLTVRatio: formatRatio(currentLTVRatio, i18n.language),
    interestAdjustmentDate: data.interestAdjustmentDate ?? '',
    loanTerm: data.loanTermPeriod,
    loanTermExpiryDate: data.loanTermExpiryDate || '',
    interestType: data.interestTypeCode.toString(),
    grandfathered: data.grandfatheredIndicator,
    loanPurposeType: data.loanPurposeType || '',
    qualifyingTotalDebtService: data.qualifyingTotalDebtService?.toString() || '',
    qualifyingGrossDebtService: data.qualifyingGrossDebtService?.toString() || '',
    originalAmortization: data.originalAmortization?.toString() || '',
    remainingAmortization: data.remainingAmortizationPeriod?.toString() || '',
    paymentResetFrequency: data.paymentResetFrequencyForArm?.toString() || '',
    interestRate: data.loanInterestRate?.toString() || ''
  };

  const showCMHCLowRatioDetails =
    Boolean(data.lowRatioProperties) &&
    Boolean(data?.lowRatioProperties?.lowRatioCmhcAccountNumber);

  const cmhcLowRatioDetails: CMHCLowRatioDataLoanDetailsExtends | undefined =
    showCMHCLowRatioDetails
      ? {
          underwritingLenderName: {
            englishName: data.underwritingLenderName.englishName,
            frenchName: data.underwritingLenderName.frenchName
          },
          accountNumber: data?.lowRatioProperties?.lowRatioCmhcAccountNumber?.toString() ?? '',
          lowRatioCmhcAccountNumber: data?.lowRatioProperties?.lowRatioCmhcAccountNumber ?? null,
          lastReferedDate: data?.lowRatioProperties?.applicationIDTimestamp ?? '',
          lowRatioResults: data?.lowRatioProperties?.lowRatioCmhcAccountNumber?.toString() ?? '',
          result: data?.lowRatioProperties?.lowRatioInsurableIndicator ?? '',
          lowRatioAge: data?.lowRatioProperties?.lowRatioAgeRangeTypeCode?.toString() ?? '',
          applicationRisk:
            data.lowRatioProperties?.startingApplicationScorecardModelRatingCode ?? '',
          propertyRisk: data?.lowRatioProperties?.propertyRisk ?? '',
          borrowerRisk: data?.lowRatioProperties?.borrowerScorecardModelRatingTypeCode ?? '',
          coborrowerRisk: data?.lowRatioProperties?.coBorrowerScorecardModelRatingTypeCode ?? '',
          borrowerDoB: data?.lowRatioReducedDatasetProperties?.borrowerBirthDate ?? '',
          coborrowerDoB: data?.lowRatioReducedDatasetProperties?.coBorrowerBirthDate ?? '',
          borrowerCreditScore: data?.lowRatioProperties?.borrowerGenericCreditScoreCount ?? '',
          coborrowerCreditScore: data?.lowRatioProperties?.coBorrowerGenericCreditScoreCount ?? '',
          propertyAddress: formatAddress(data?.lowRatioReducedDatasetProperties?.propertyAddress),
          noOfUnits:
            data?.lowRatioReducedDatasetProperties?.propertyTotalUnitCount.toString() ?? '',
          dwellingType: data?.lowRatioReducedDatasetProperties?.propertyDwellingTypeCode ?? '',
          tenureType: data?.lowRatioReducedDatasetProperties?.propertyTenureTypeCode ?? '',
          originalLoanAmount: formatAmountWithDollar(
            data.lowRatioReducedDatasetProperties?.lenderLoanAmount?.toString() ?? '',
            i18n.language
          ),
          originalLendingValue: formatAmountWithDollar(
            data?.lowRatioReducedDatasetProperties?.propertyLendingValue?.toString() ?? '',
            i18n.language
          ),
          loanPurposeType: data.lowRatioReducedDatasetProperties?.purposeTypeCode ?? ''
        }
      : undefined;

  const otherLoanData: OtherLoanDetails | undefined = {
    multiComponentKey: data.multipleComponentText ?? '',
    mortgageSecurityPriorityRanking: data.mortgageSecurityPriorityRanking?.toString() || '',
    mortgageSecurityType: data.mortgageSecurityType ?? '',
    propertyValuationMethod: data.propertyValuationMethod || '',
    propertyValuationMethodOther: data.propertyValuationMethodOtherDescription || '',
    mortgatePaymentFrequency: data.mortgagePaymentFrequency || '',
    mortgatePrincipalAndInterestPaymentAmount: formatAmountWithDollar(
      data.mortgagePrincipalAndInterestPaymentAmountNoTax?.toString() || '',
      i18n.language
    ),
    residentialStatus: data.residentialStatus || '',
    propertyListingType: data.propertyListingType || '',
    sourceOfBusiness: data.sourceOfBusiness || '',
    brokerMortgageSpecialistName: `${data.brokerMortgageSpecialistFirstName ?? ''} ${data.brokerMortgageSpecialistLastName ?? ''}`
  };

  return {
    borrowerLoanDetails,
    lenderDetails,
    propertyDetails,
    keyLoanDetails,
    cmhcLowRatioDetails,
    otherLoanData
  };
};
